import { useAuth } from "@/context/AuthContext";
import { Navigate, useLocation } from "react-router-dom";
import { tr } from "@/lib/i18n";
import LegalConsentGate from "@/components/LegalConsentGate";

export default function ProtectedRoute({ children }) {
  const { user, loading, lang, access } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-950">
        <div className="mono text-stone-500 text-sm tracking-widest">{tr("loading.stage", lang)}</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  // Spec §1/§2: one-shot first-onboarding guard. Any FREE user (or PAID
  // registered after the cutoff) without `leo_first_onboarding_completed`
  // is routed to /leo?src=first-onboarding on ANY authenticated entry
  // point. Allow-list the onboarding URLs themselves — both `/leo` and
  // `/leo/{id}` when the `src=first-onboarding` marker is present — to
  // avoid a redirect loop while the onboarding runs.
  // DEADLOCK FIX: only enforce the redirect when the user can actually
  // complete the onboarding right now — i.e. has LEO access AND chat
  // quota is not exhausted. Otherwise (expired 7-day trial, or quota
  // used up) do NOT trap them on the onboarding screen; the rest of the
  // app is still guarded by its own per-module paywalls / plan limits.
  // Fail-open: while `access` is still null (first boot), don't redirect.
  if (user?.needs_first_onboarding === true) {
    const onLeo = location.pathname === "/leo" || location.pathname.startsWith("/leo/");
    const src = new URLSearchParams(location.search).get("src");
    const onFirstOnboarding = onLeo && src === "first-onboarding";
    const cap = access?.limits?.chat_messages ?? null; // null = plan without cap
    const used = access?.usage?.chat_messages_used_period ?? 0;
    const quotaExhausted = cap !== null && used >= cap;
    const canCompleteOnboarding = access?.has_access === true && !quotaExhausted;
    // Loosened guard: never redirect while the user is on ANY /leo path
    // (including /leo, /leo/{id}, with or without ?src=first-onboarding).
    // Prevents "trapped in LEO" when finishing/reopening onboarding.
    if (canCompleteOnboarding && !onFirstOnboarding && !onLeo) {
      return <Navigate to="/leo?src=first-onboarding" replace />;
    }
  }
  // Wrap every authenticated screen with the one-shot legal consent gate.
  // Users with a valid checkout_consents OR user_legal_consents row for
  // the current TERMS_VERSION pass through instantly; everyone else sees
  // a blocking modal exactly once.
  return <LegalConsentGate>{children}</LegalConsentGate>;
}
